/**
 * The remote picture: aircraft other nodes fly, and signals they detected.
 *
 * Kept apart from droneStore on purpose. Everything in droneStore is one of
 * ours and can be selected, followed and commanded; nothing here can, and
 * keeping the two in different stores is what stops a remote aircraft from
 * ever reaching a control that takes a `DroneId`.
 *
 * Only admins and observers are shown other nodes (the server refuses everyone
 * else, since another node's teams mean nothing here), so the poll is only
 * started for them.
 */

import { create } from 'zustand';

import {
  getRemoteAircraft,
  getSignals,
  type IsrSignal,
  type RemoteAircraft,
} from '@/api/federation';
import { useAuthStore } from '@/stores/authStore';

/** Pushes arrive every few seconds; polling faster only redraws the same picture. */
export const REMOTE_POLL_MS = 4000;
/** Signals on the map: emitters heard in the last ten minutes. The panel reads further back. */
export const MAP_SIGNAL_WINDOW_S = 600;

const NO_AIRCRAFT: RemoteAircraft[] = [];
const NO_SIGNALS: IsrSignal[] = [];

interface FederationState {
  aircraft: RemoteAircraft[];
  signals: IsrSignal[];
  error: string | null;
  loadedAt: number | null;
}

export const useFederationStore = create<FederationState>(() => ({
  aircraft: NO_AIRCRAFT,
  signals: NO_SIGNALS,
  error: null,
  loadedAt: null,
}));

export function canSeeRemote(user: { role: string; observer?: boolean } | null): boolean {
  return !!user && (user.role === 'admin' || user.observer === true);
}

export function useCanSeeRemote(): boolean {
  return useAuthStore((state) => canSeeRemote(state.user));
}

export const federationActions = {
  async load(): Promise<void> {
    const [aircraft, signals] = await Promise.all([
      getRemoteAircraft(),
      getSignals({ sinceS: MAP_SIGNAL_WINDOW_S, limit: 1000 }),
    ]);
    useFederationStore.setState({
      aircraft: aircraft.ok ? aircraft.data.aircraft : NO_AIRCRAFT,
      signals: signals.ok ? signals.data.signals : NO_SIGNALS,
      error: aircraft.ok ? null : aircraft.error.message,
      loadedAt: Date.now() / 1000,
    });
  },
  clear(): void {
    useFederationStore.setState({ aircraft: NO_AIRCRAFT, signals: NO_SIGNALS, error: null });
  },
};
